import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { TextField, Toggle, Section, Row, SaveBar, EyebrowSizeField } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export default function RegistrationTab() {
  const [reg, setReg] = useState(null);
  const [indExps, setIndExps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadAll = async () => {
    const r = await api.get('/admin/settings');
    setReg(r.data?.registration || {});
    const rExp = await api.get('/admin/registration/experiences');
    setIndExps(rExp.data || []);
  };
  useEffect(() => { loadAll(); }, []);

  if (!reg) return <div className="font-body text-ink-muted">Loading…</div>;

  const fa = reg.full_access || {};
  const hotel = reg.hotel || {};

  const setReg2 = (k, v) => setReg({ ...reg, [k]: v });
  const setFA = (k, v) => setReg({ ...reg, full_access: { ...fa, [k]: v } });
  const setHotel = (k, v) => setReg({ ...reg, hotel: { ...hotel, [k]: v } });

  // v3.15 — Eyebrow size commit handler.
  // n === null → remove the key entirely from `reg` so the outgoing
  // /admin/settings/registration payload has no `eyebrow_size` and the
  // site falls back to the 26px default. Any integer 16..48 is stored
  // as-is; child EyebrowSizeField has already rejected everything else.
  const commitEyebrowSize = (n) => {
    if (n === null) {
      const next = { ...reg };
      delete next.eyebrow_size;
      setReg(next);
    } else {
      setReg({ ...reg, eyebrow_size: n });
    }
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      // Persist labels/full access/hotel - but DO NOT clobber individual_experiences
      // (managed via dedicated CRUD endpoints).
      const payload = { ...reg, individual_experiences: indExps };
      await api.put('/admin/settings/registration', payload);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  // --- Individual experience CRUD via dedicated endpoints ---
  const addNew = () => setEditing({ title: '', price: '', image: '', url: '#', visible: true, coming_soon: false, order: indExps.length });
  const startEdit = (it) => setEditing({ ...it });

  const remove = async (id) => {
    if (!window.confirm('Delete this individual experience?')) return;
    await api.delete(`/admin/registration/experiences/${id}`);
    loadAll();
  };

  const move = async (id, dir) => {
    const idx = indExps.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= indExps.length) return;
    const next = [...indExps];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setIndExps(next);
    await api.put('/admin/registration/experiences/reorder', { items: next.map((it, i) => ({ id: it.id, order: i })) });
    loadAll();
  };

  const saveItem = async () => {
    setSavingItem(true);
    try {
      const payload = {
        title: editing.title,
        price: editing.price,
        image: editing.image || '',
        url: editing.url || '#',
        visible: editing.visible !== false,
        coming_soon: !!editing.coming_soon,
        order: typeof editing.order === 'number' ? editing.order : indExps.length,
      };
      if (editing.id) {
        await api.put(`/admin/registration/experiences/${editing.id}`, payload);
      } else {
        await api.post('/admin/registration/experiences', payload);
      }
      setEditing(null);
      loadAll();
    } finally { setSavingItem(false); }
  };

  return (
    <div>
      <Section title="Full Access Pass">
        <Row>
          <TextField label="Pass Name (e.g. Full Access Pass)" value={fa.name} onChange={(v) => setFA('name', v)} />
          <TextField label="Best Value Badge Label" value={fa.best_value_label} onChange={(v) => setFA('best_value_label', v)} />
        </Row>
        <Row>
          <TextField label="Price" value={fa.price} onChange={(v) => setFA('price', v)} />
          <TextField label="Deadline Text" value={fa.deadline} onChange={(v) => setFA('deadline', v)} />
        </Row>
        <TextField label="Description" value={fa.description} onChange={(v) => setFA('description', v)} multiline />
        <TextField label="Bullets (one per line)" value={(fa.bullets || []).join('\n')} onChange={(v) => setFA('bullets', v.split('\n').map((s) => s.trim()).filter(Boolean))} multiline />
        <Row>
          <TextField label="Select Button Label" value={fa.select_label} onChange={(v) => setFA('select_label', v)} />
          <TextField label="Swoogo Registration URL" value={fa.swoogo_url} onChange={(v) => setFA('swoogo_url', v)} />
        </Row>
      </Section>

      <Section title="Individual Experiences (Section Labels)">
        <Row>
          <TextField label="Section Eyebrow" value={reg.upgrade_eyebrow} onChange={(v) => setReg2('upgrade_eyebrow', v)} />
          <TextField label="Per-Card 'Learn More' Label" value={reg.individual_learn_more_label} onChange={(v) => setReg2('individual_learn_more_label', v)} />
        </Row>
        <TextField label="Per-Card 'Coming Soon' Badge Label" value={reg.individual_coming_soon_label} onChange={(v) => setReg2('individual_coming_soon_label', v)} />
        <Row>
          <TextField label="Upgrade Box Title" value={reg.upgrade_box_title} onChange={(v) => setReg2('upgrade_box_title', v)} />
          <TextField label="Upgrade Box Button Label" value={reg.upgrade_box_button_label} onChange={(v) => setReg2('upgrade_box_button_label', v)} />
        </Row>
        <TextField label="Upgrade Box Body" value={reg.upgrade_box_body} onChange={(v) => setReg2('upgrade_box_body', v)} multiline />
        <EyebrowSizeField
          label="Registration Eyebrow Size (px)"
          value={reg.eyebrow_size}
          onCommit={commitEyebrowSize}
          testid="registration-eyebrow-size"
          hint="Controls only the orange EYEBROW label above the Registration headline (&quot;ONE REGISTRATION PLATFORM.&quot;)."
        />
      </Section>

      <Section title="Individual Experience Cards" action={<button onClick={addNew} className="btn-primary text-[12px] !py-2.5 !px-4" data-testid="add-ind-experience"><Plus size={14} className="mr-1" /> Add Card</button>}>
        <div className="space-y-3">
          {indExps.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white" data-testid="ind-experience-row">
              <div className="w-16 h-16 rounded-sm overflow-hidden bg-bg-canvas shrink-0">
                {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold uppercase text-ink-primary text-[14px] tracking-tightish">{it.title}</div>
                <div className="font-body text-[12px] text-ink-muted">{it.price}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-body text-[11px] text-ink-soft uppercase">{it.visible !== false ? 'Visible' : 'Hidden'}</span>
                  {it.coming_soon && <span className="font-body text-[10px] uppercase tracking-wider2 bg-brand-orange text-white px-1.5 py-0.5 rounded">Coming Soon</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === indExps.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => startEdit(it)} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
              <button onClick={() => remove(it.id)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Hotel Card">
        <ImageUploader value={hotel.image} onChange={(v) => setHotel('image', v)} label="Hotel Image" />
        <Row>
          <TextField label="Hotel Card Eyebrow" value={reg.hotel_eyebrow} onChange={(v) => setReg2('hotel_eyebrow', v)} />
          <TextField label="Hotel Name" value={hotel.name} onChange={(v) => setHotel('name', v)} />
        </Row>
        <Row>
          <TextField label="Booking Deadline" value={hotel.deadline} onChange={(v) => setHotel('deadline', v)} />
          <TextField label="Book Button Label" value={reg.hotel_select_label} onChange={(v) => setReg2('hotel_select_label', v)} />
        </Row>
        <TextField label="Perks (one per line)" value={(hotel.perks || []).join('\n')} onChange={(v) => setHotel('perks', v.split('\n').map((s) => s.trim()).filter(Boolean))} multiline />
        <TextField label="Bellagio Booking URL" value={hotel.booking_url} onChange={(v) => setHotel('booking_url', v)} />
      </Section>

      <SaveBar onSave={save} saving={saving} saved={saved} />

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">{editing.id ? 'Edit Individual Experience' : 'New Individual Experience'}</h3>
            <ImageUploader value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} label="Card Image" />
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextField label="Price" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} />
            </div>
            <div className="mt-4">
              <TextField label="Learn More URL" value={editing.url} onChange={(v) => setEditing({ ...editing, url: v })} />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Toggle label="Visible" value={editing.visible !== false} onChange={(v) => setEditing({ ...editing, visible: v })} />
              <Toggle label="Coming Soon (disables Learn More, shows badge)" value={!!editing.coming_soon} onChange={(v) => setEditing({ ...editing, coming_soon: v })} />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={saveItem} disabled={savingItem} className="btn-primary text-[12px] !py-2 !px-4">{savingItem ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
